<script setup>
import { ref, computed, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useKanbanStore } from 'dashboard/stores/kanban';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

// CRM-04 R2-T7 (N15): "Juntar com…", só para o administrador (a recusa é do servidor). O negócio aberto
// no painel fica; o escolhido entrega a ele os contatos, as conversas e o histórico e sai do quadro. O
// negócio em Ganho ou Perdido não pode ser o absorvido: aparece, mas não se escolhe.
const props = defineProps({
  card: { type: Object, required: true },
  stages: { type: Array, default: () => [] },
});

const emit = defineEmits(['merged']);

const { t } = useI18n();
const kanban = useKanbanStore();

const dialogRef = ref(null);
const busca = ref('');
const candidatos = ref([]);
const escolhido = ref(null);
const buscou = ref(false);
const juntando = ref(false);
const erro = ref('');

const nomeDe = negocio =>
  negocio.display_name || negocio.title || t('KANBAN.CARD.NO_NAME');

const etapaDe = negocio =>
  props.stages.find(stage => stage.id === negocio.stage_id);

const fechado = negocio => ['won', 'lost'].includes(etapaDe(negocio)?.behavior);

const nomeDoCard = computed(() => nomeDe(props.card));

let espera = null;
let pedido = 0;

const pesquisar = async () => {
  clearTimeout(espera);
  pedido += 1;
  const meu = pedido;
  try {
    const achados = await kanban.searchDeals(
      props.card.pipeline_id,
      busca.value.trim()
    );
    // Uma resposta velha não passa por cima da busca mais nova.
    if (meu !== pedido) return;
    candidatos.value = achados.filter(negocio => negocio.id !== props.card.id);
    buscou.value = true;
  } catch {
    if (meu === pedido) erro.value = t('KANBAN.MERGE.SEARCH_FAILED');
  }
};

const aoDigitar = () => {
  clearTimeout(espera);
  espera = setTimeout(pesquisar, 300);
};

onBeforeUnmount(() => clearTimeout(espera));

// `preescolhido` vem do 409 de "adicionar contato": o negócio aberto do contato, só com o id e um nome.
const open = (preescolhido = null) => {
  busca.value = '';
  erro.value = '';
  buscou.value = false;
  escolhido.value = preescolhido;
  candidatos.value = preescolhido ? [preescolhido] : [];
  dialogRef.value?.open();
  if (!preescolhido) pesquisar();
};

const escolher = negocio => {
  if (fechado(negocio)) return;
  escolhido.value = negocio;
};

// O servidor recusa a permissão com 403 e `admin_only` (no OS, 401 quer dizer sessão inválida) e as regras do
// juntar com 422 e o código em `error` (`Kanban::Juntar::Recusa`), sem `message`.
const recusaAoJuntar = error => {
  const status = error?.response?.status;
  if (status === 403 && error.response.data?.error === 'admin_only')
    return t('KANBAN.MERGE.ADMIN_ONLY');
  if (status !== 422) return t('KANBAN.MERGE.FAILED');
  const recusas = {
    same_card: t('KANBAN.MERGE.SAME_CARD'),
    other_pipeline: t('KANBAN.MERGE.OTHER_PIPELINE'),
    absorbed_closed: t('KANBAN.MERGE.ABSORBED_CLOSED'),
  };
  return recusas[error.response.data?.error] || t('KANBAN.MERGE.FAILED');
};

const confirmar = async () => {
  if (!escolhido.value || juntando.value) return;
  const absorvido = escolhido.value;
  juntando.value = true;
  erro.value = '';
  try {
    await kanban.mergeCards(props.card.id, absorvido.id);
  } catch (error) {
    erro.value = recusaAoJuntar(error);
    return;
  } finally {
    juntando.value = false;
  }
  useAlert(t('KANBAN.MERGE.DONE', { name: nomeDe(absorvido) }));
  dialogRef.value?.close();
  emit('merged', props.card.id);
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="$t('KANBAN.MERGE.TITLE')"
    :description="$t('KANBAN.MERGE.DESCRIPTION', { name: nomeDoCard })"
    :confirm-button-label="$t('KANBAN.MERGE.CONFIRM')"
    :disable-confirm-button="!escolhido"
    :is-loading="juntando"
    @confirm="confirmar"
  >
    <div class="flex flex-col gap-2">
      <input
        v-model="busca"
        type="search"
        data-busca-negocio
        :placeholder="$t('KANBAN.MERGE.SEARCH')"
        :aria-label="$t('KANBAN.MERGE.SEARCH')"
        class="w-full h-9 px-2.5 text-sm rounded-lg reset-base bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
        @input="aoDigitar"
        @keydown.enter.prevent="pesquisar"
      />
      <div class="flex flex-col gap-1 overflow-y-auto max-h-64">
        <button
          v-for="negocio in candidatos"
          :key="negocio.id"
          type="button"
          data-candidato
          :disabled="fechado(negocio)"
          :aria-pressed="escolhido?.id === negocio.id"
          class="flex items-center gap-2 px-2 py-1.5 text-sm text-left rounded-md outline outline-1 disabled:opacity-50 disabled:cursor-not-allowed"
          :class="
            escolhido?.id === negocio.id
              ? 'outline-n-brand bg-n-alpha-2'
              : 'outline-transparent hover:bg-n-alpha-1'
          "
          @click="escolher(negocio)"
        >
          <span class="flex-1 truncate text-n-slate-12">
            {{ nomeDe(negocio) }}
          </span>
          <span
            v-if="fechado(negocio)"
            class="flex-shrink-0 text-xs text-n-slate-10"
          >
            {{ $t('KANBAN.MERGE.CLOSED') }}
          </span>
          <span
            v-else-if="etapaDe(negocio)"
            class="flex-shrink-0 text-xs text-n-slate-10"
          >
            {{ etapaDe(negocio).name }}
          </span>
        </button>
      </div>
      <p v-if="buscou && !candidatos.length" class="text-xs text-n-slate-10">
        {{ $t('KANBAN.MERGE.NOTHING_FOUND') }}
      </p>
      <p v-if="erro" data-erro class="text-xs text-n-ruby-11">{{ erro }}</p>
    </div>
  </Dialog>
</template>
